import { faWandMagicSparkles } from '@fortawesome/pro-solid-svg-icons';
import { Icon } from '../../../../components/base/Icon';
import { Text } from '../../../../components/base/Text';
import { Card } from '../../../../components/display/Card';
import { Input } from '../../../../components/forms/Input';
import { Select } from '../../../../components/forms/Select';
import { useThemeColors } from '../../../../providers/ThemeProvider';
import { cn } from '../../../../utils/cn';
import type {
	FieldConfiguration,
	FormatOptions,
	ListBuilderConfiguration,
	StyleConfiguration,
} from '../../../types';

interface FormatOptionsConfiguratorProps {
	field: FieldConfiguration;
	configuration?: ListBuilderConfiguration;
	onUpdateField: (updates: Partial<FieldConfiguration>) => void;
	isModalOpen?: boolean;
}

export function FormatOptionsConfigurator({
	field,
	configuration,
	onUpdateField,
	isModalOpen = false,
}: FormatOptionsConfiguratorProps) {
	const colors = useThemeColors();
	// styleConfig is created alongside the field (see DisplayStyleSelector);
	// default defensively so a config that omits it can't crash the editor.
	const styleConfig: StyleConfiguration = field.styleConfig ?? { size: 'md' };
	const formatOptions = styleConfig.formatOptions;

	const updateFormatOptions = (updates: Partial<FormatOptions>): void => {
		onUpdateField({
			styleConfig: {
				...styleConfig,
				formatOptions: {
					...formatOptions,
					...updates,
				},
			},
		});
	};
	// Get available fields for relative date reference
	const getAvailableFields = () => {
		if (!configuration?.objects || configuration.objects.length === 0) {
			return [];
		}

		const fields: Array<{
			fieldName: string;
			fieldLabel: string;
			objectTable: string;
			objectLabel: string;
		}> = [];

		configuration.objects.forEach((obj) => {
			obj.selectedFields.forEach((field) => {
				fields.push({
					fieldName: field.fieldName,
					fieldLabel: field.fieldLabel,
					objectTable: obj.objectTable,
					objectLabel: obj.objectLabel,
				});
			});
		});

		return fields;
	};

	// Don't render if not applicable display style
	if (
		!['date', 'relative_date', 'number', 'currency', 'percent'].includes(
			field.displayStyle,
		)
	) {
		return null;
	}

	const availableFields = getAvailableFields();

	return (
		<Card className="mb-4">
			<div className="p-4">
				<div className="flex items-center mb-3">
					<Icon
						icon={faWandMagicSparkles}
						size="sm"
						color={colors.values.textPrimary}
						className="mr-2"
					/>
					<Text className="text-base font-semibold">Format Options</Text>
				</div>

				{field.displayStyle === 'date' && (
					<div>
						<Text className="text-sm font-medium mb-2">Date Format</Text>
						<div className="space-y-2">
							<Text className="text-xs mb-1" color="secondary">
								Date Only Formats
							</Text>
							{(
								[
									{
										value: 'short',
										label: 'Short (6/13/25)',
										example: new Date(),
									},
									{
										value: 'medium',
										label: 'Medium (Jun 13, 2025)',
										example: new Date(),
									},
									{
										value: 'long',
										label: 'Long (June 13, 2025)',
										example: new Date(),
									},
									{
										value: 'iso',
										label: 'ISO (2025-06-13)',
										example: new Date(),
									},
								] as const
							).map((option) => (
								<button
									key={option.value}
									onClick={() =>
										updateFormatOptions({ dateFormat: option.value })
									}
									className={cn(
										'w-full flex items-center p-3 rounded-lg border transition-colors',
										formatOptions?.dateFormat === option.value
											? cn(colors.classes.bgInfo, colors.classes.borderPrimary)
											: cn(
													colors.classes.bgSecondary,
													colors.classes.borderDefault,
													colors.classes.bgPrimaryHover,
												),
									)}
								>
									<div
										className={cn(
											'w-4 h-4 rounded-full border-2 mr-2',
											formatOptions?.dateFormat === option.value
												? cn(
														colors.classes.borderPrimary,
														colors.classes.bgPrimary,
													)
												: colors.classes.borderDefault,
										)}
									/>
									<Text
										className={cn(
											'text-sm',
											formatOptions?.dateFormat === option.value &&
												'font-semibold',
										)}
									>
										{option.label}
									</Text>
								</button>
							))}

							<Text className="block text-xs mt-3 mb-1" color="secondary">
								Date & Time Formats
							</Text>
							{(
								[
									{
										value: 'datetime-short',
										label: 'Short (6/13/25 2:30 PM)',
										example: new Date(),
									},
									{
										value: 'datetime-medium',
										label: 'Medium (Jun 13, 2025 2:30 PM)',
										example: new Date(),
									},
									{
										value: 'datetime-long',
										label: 'Long (June 13, 2025 at 2:30:45 PM)',
										example: new Date(),
									},
									{
										value: 'datetime-iso',
										label: 'ISO (2025-06-13T14:30:45)',
										example: new Date(),
									},
								] as const
							).map((option) => (
								<button
									key={option.value}
									onClick={() =>
										updateFormatOptions({ dateFormat: option.value })
									}
									className={cn(
										'w-full flex items-center p-3 rounded-lg border transition-colors',
										formatOptions?.dateFormat === option.value
											? cn(colors.classes.bgInfo, colors.classes.borderPrimary)
											: cn(
													colors.classes.bgSecondary,
													colors.classes.borderDefault,
													colors.classes.bgPrimaryHover,
												),
									)}
								>
									<div
										className={cn(
											'w-4 h-4 rounded-full border-2 mr-2',
											formatOptions?.dateFormat === option.value
												? cn(
														colors.classes.borderPrimary,
														colors.classes.bgPrimary,
													)
												: colors.classes.borderDefault,
										)}
									/>
									<Text
										className={cn(
											'text-sm',
											formatOptions?.dateFormat === option.value &&
												'font-semibold',
										)}
									>
										{option.label}
									</Text>
								</button>
							))}
						</div>
					</div>
				)}

				{field.displayStyle === 'relative_date' && (
					<div>
						<Text className="text-sm font-medium mb-2">Display Unit</Text>
						<div className="space-y-2 mb-4">
							{(
								[
									{ value: 'auto', label: 'Auto' },
									{ value: 'day', label: 'Days' },
									{ value: 'week', label: 'Weeks' },
									{ value: 'month', label: 'Months' },
									{ value: 'year', label: 'Years' },
								] as const
							).map((option) => {
								const effectiveUnit = formatOptions?.relativeUnit ?? 'auto';
								const isSelected = effectiveUnit === option.value;

								return (
									<button
										key={option.value}
										onClick={() =>
											onUpdateField({
												styleConfig: {
													...styleConfig,
													formatOptions: {
														...formatOptions,
														relativeUnit: option.value,
													},
												},
											})
										}
										className={cn(
											'w-full flex items-center p-3 rounded-lg border transition-colors',
											isSelected
												? cn(
														colors.classes.bgInfo,
														colors.classes.borderPrimary,
													)
												: cn(
														colors.classes.bgSecondary,
														colors.classes.borderDefault,
														colors.classes.bgPrimaryHover,
													),
										)}
									>
										<div
											className={cn(
												'w-4 h-4 rounded-full border-2 mr-2 flex items-center justify-center',
												isSelected
													? cn(
															colors.classes.borderPrimary,
															colors.classes.bgPrimary,
														)
													: colors.classes.borderDefault,
											)}
										>
											{isSelected && (
												<div className="w-2 h-2 rounded-full bg-white" />
											)}
										</div>
										<Text
											className={cn('text-sm', isSelected && 'font-semibold')}
										>
											{option.label}
										</Text>
									</button>
								);
							})}
						</div>

						<Text className="text-sm font-medium mb-2">
							Calculate Relative To
						</Text>
						<div className="space-y-2 mb-4">
							<button
								onClick={() =>
									onUpdateField({
										styleConfig: {
											...styleConfig,
											formatOptions: {
												...formatOptions,
												relativeBase: 'today',
											},
										},
									})
								}
								className={cn(
									'w-full flex items-center p-3 rounded-lg border transition-colors',
									formatOptions?.relativeBase !== 'field'
										? cn(colors.classes.bgInfo, colors.classes.borderPrimary)
										: cn(
												colors.classes.bgSecondary,
												colors.classes.borderDefault,
												colors.classes.bgPrimaryHover,
											),
								)}
							>
								<div
									className={cn(
										'w-4 h-4 rounded-full border-2 mr-2',
										formatOptions?.relativeBase !== 'field'
											? cn(
													colors.classes.borderPrimary,
													colors.classes.bgPrimary,
												)
											: colors.classes.borderDefault,
									)}
								/>
								<Text
									className={cn(
										'text-sm',
										formatOptions?.relativeBase !== 'field' && 'font-semibold',
									)}
								>
									Today's Date
								</Text>
							</button>

							<button
								onClick={() =>
									onUpdateField({
										styleConfig: {
											...styleConfig,
											formatOptions: {
												...formatOptions,
												relativeBase: 'field',
											},
										},
									})
								}
								className={cn(
									'w-full flex items-center p-3 rounded-lg border transition-colors',
									formatOptions?.relativeBase === 'field'
										? cn(colors.classes.bgInfo, colors.classes.borderPrimary)
										: cn(
												colors.classes.bgSecondary,
												colors.classes.borderDefault,
												colors.classes.bgPrimaryHover,
											),
								)}
							>
								<div
									className={cn(
										'w-4 h-4 rounded-full border-2 mr-2',
										formatOptions?.relativeBase === 'field'
											? cn(
													colors.classes.borderPrimary,
													colors.classes.bgPrimary,
												)
											: colors.classes.borderDefault,
									)}
								/>
								<Text
									className={cn(
										'text-sm',
										formatOptions?.relativeBase === 'field' && 'font-semibold',
									)}
								>
									Another Field
								</Text>
							</button>
						</div>

						{formatOptions?.relativeBase === 'field' && (
							<div className="mb-4">
								<Text className="block text-xs font-medium mb-1" as="label">
									Reference Field
								</Text>
								<Select
									value={
										formatOptions?.relativeField != null
											? typeof formatOptions.relativeField === 'string'
												? formatOptions.relativeField
												: `${formatOptions.relativeField.objectTable}:${formatOptions.relativeField.fieldName}`
											: undefined
									}
									options={availableFields.map((f) => ({
										value: `${f.objectTable}:${f.fieldName}`,
										label: `${f.objectLabel}: ${f.fieldLabel}`,
									}))}
									onChange={(value) => {
										const [objectTable = '', fieldName = ''] = value.split(':');
										updateFormatOptions({
											relativeField: { objectTable, fieldName },
										});
									}}
									placeholder="Select reference date field..."
								/>
							</div>
						)}

						<div className="mb-4">
							<Text className="block text-xs font-medium mb-1" as="label">
								Text When In The Past
							</Text>
							<Input
								type="text"
								value={formatOptions?.pastText ?? ''}
								onChange={(e) =>
									onUpdateField({
										styleConfig: {
											...styleConfig,
											formatOptions: {
												...formatOptions,
												pastText: e.target.value,
											},
										},
									})
								}
								placeholder="e.g. Overdue"
								size="sm"
							/>
							<Text className="text-xs mt-1" color="secondary">
								Shown instead of the timespan when the date is in the past.
								Leave blank to use the formatted timespan.
							</Text>
						</div>

						<div className="mb-4">
							<Text className="block text-xs font-medium mb-1" as="label">
								Text When In The Future
							</Text>
							<Input
								type="text"
								value={formatOptions?.futureText ?? ''}
								onChange={(e) =>
									onUpdateField({
										styleConfig: {
											...styleConfig,
											formatOptions: {
												...formatOptions,
												futureText: e.target.value,
											},
										},
									})
								}
								placeholder="e.g. Upcoming"
								size="sm"
							/>
							<Text className="text-xs mt-1" color="secondary">
								Shown instead of the timespan when the date is in the future.
								Leave blank to use the formatted timespan.
							</Text>
						</div>
					</div>
				)}

				{(field.displayStyle === 'number' ||
					field.displayStyle === 'currency' ||
					field.displayStyle === 'percent') && (
					<div>
						<Text className="text-sm font-medium mb-2">
							Number Format Options
						</Text>

						{/* Number Format Style */}
						<div className="mb-4">
							<Text className="text-xs mb-2" color="secondary">
								Format Style
							</Text>
							<div className="space-y-2">
								{(() => {
									// Currency defaults to 'decimal', number/percent default to 'auto'
									const defaultFormat =
										field.displayStyle === 'currency' ? 'decimal' : 'auto';
									const effectiveFormat =
										formatOptions?.numberFormat ?? defaultFormat;

									return (
										[
											{ value: 'auto', label: 'Auto (1,234.5)' },
											{ value: 'decimal', label: 'Decimal (1,234.56)' },
											{ value: 'scientific', label: 'Scientific (1.23e+3)' },
										] as const
									).map((option) => {
										const isSelected = effectiveFormat === option.value;

										return (
											<button
												key={option.value}
												onClick={() =>
													updateFormatOptions({ numberFormat: option.value })
												}
												className={cn(
													'w-full flex items-center p-3 rounded-lg border transition-colors',
													isSelected
														? cn(
																colors.classes.bgInfo,
																colors.classes.borderPrimary,
															)
														: cn(
																colors.classes.bgSecondary,
																colors.classes.borderDefault,
																colors.classes.bgPrimaryHover,
															),
												)}
											>
												<div
													className={cn(
														'w-4 h-4 rounded-full border-2 mr-2 flex items-center justify-center',
														isSelected
															? cn(
																	colors.classes.borderPrimary,
																	colors.classes.bgPrimary,
																)
															: colors.classes.borderDefault,
													)}
												>
													{isSelected && (
														<div className="w-2 h-2 rounded-full bg-white" />
													)}
												</div>
												<Text
													className={cn(
														'text-sm',
														isSelected && 'font-semibold',
													)}
												>
													{option.label}
												</Text>
											</button>
										);
									});
								})()}
							</div>
						</div>

						{/* Percent: value is whole number toggle */}
						{field.displayStyle === 'percent' && (
							<div className="mb-4">
								<Text className="text-xs mb-2" color="secondary">
									Value Interpretation
								</Text>
								<button
									onClick={() =>
										onUpdateField({
											styleConfig: {
												...styleConfig,
												formatOptions: {
													...formatOptions,
													percentValueIsWholeNumber:
														formatOptions?.percentValueIsWholeNumber === false,
												},
											},
										})
									}
									className={cn(
										'w-full flex items-center p-2 rounded-md border transition-colors',
										formatOptions?.percentValueIsWholeNumber === false
											? cn(colors.classes.bgInfo, colors.classes.borderPrimary)
											: cn(
													colors.classes.bgSecondary,
													colors.classes.borderDefault,
													colors.classes.bgPrimaryHover,
												),
									)}
								>
									<div
										className={cn(
											'w-4 h-4 rounded border-2 mr-2 flex items-center justify-center shrink-0',
											formatOptions?.percentValueIsWholeNumber === false
												? cn(
														colors.classes.borderPrimary,
														colors.classes.bgPrimary,
													)
												: colors.classes.borderDefault,
										)}
									>
										{formatOptions?.percentValueIsWholeNumber === false && (
											<div className="w-2 h-2 bg-white rounded-sm" />
										)}
									</div>
									<Text
										className={cn(
											'text-sm',
											formatOptions?.percentValueIsWholeNumber === false &&
												'font-medium',
										)}
									>
										Multiply by 100
									</Text>
								</button>
								<Text className="text-xs mt-1" color="secondary">
									Enable if the raw value is a decimal (e.g. 0.5 = 50%)
								</Text>
							</div>
						)}

						{/* Decimal Places - hidden for auto format since it determines decimals automatically */}
						{formatOptions?.numberFormat !== 'auto' &&
							!(
								field.displayStyle === 'number' && !formatOptions?.numberFormat
							) && (
								<div className="mb-4">
									<Text
										className="block text-xs mb-2"
										color="secondary"
										as="label"
									>
										Decimal Places
									</Text>
									<Input
										type="number"
										value={formatOptions?.decimalPlaces?.toString() || '2'}
										onChange={(e) => {
											const num = parseInt(e.target.value) || 0;
											if (num >= 0 && num <= 10) {
												onUpdateField({
													styleConfig: {
														...styleConfig,
														formatOptions: {
															...formatOptions,
															decimalPlaces: num,
														},
													},
												});
											}
										}}
										min="0"
										max="10"
										placeholder="2"
										size="sm"
									/>
								</div>
							)}

						{/* Currency Symbol (only for currency format) */}
						{field.displayStyle === 'currency' && (
							<div className="mb-4">
								<Text
									className="block text-xs mb-2"
									color="secondary"
									as="label"
								>
									Currency Symbol
								</Text>
								<Input
									type="text"
									value={formatOptions?.currencySymbol || '$'}
									onChange={(e) =>
										onUpdateField({
											styleConfig: {
												...styleConfig,
												formatOptions: {
													...formatOptions,
													currencySymbol: e.target.value,
												},
											},
										})
									}
									placeholder="$"
									size="sm"
								/>
							</div>
						)}
					</div>
				)}
			</div>
		</Card>
	);
}
